import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { ArrowUp } from "lucide-react";
import { useI18n } from "@/i18n";

/**
 * 回到頂端
 * ═══════════════════════════════════════════════════════════════
 *
 * 案件列表改成累加式分頁之後，捲到第 5 次「載入更多」大概是 100 張
 * 卡片。要回到篩選器就得往上滑很久 —— 這顆按鈕是那個改動的配套，
 * 不是裝飾。
 *
 * ── 什麼時候出現 ────────────────────────────────────────────
 *
 * 捲超過兩個螢幕高才出現。門檻設成螢幕高度的倍數而不是固定 px：
 * 手機一個螢幕約 700px、桌機約 1000px，固定值在其中一邊一定不對。
 *
 * 而且要「往上捲」才出現。往下捲時使用者正在讀，這時候在角落塞一顆
 * 按鈕是打斷；往上捲代表他在找東西 —— 那才是這顆按鈕有用的時刻。
 *
 * ── 動畫 ────────────────────────────────────────────────────
 *
 * 進場從下方帶旋轉推上來，退場直接縮小。旋轉是為了讓箭頭的方向感
 * 在出現的瞬間被注意到 —— 靜態出現的圖示很容易被當成背景。
 *
 * 減少動態時仍然出現（它是功能不是效果），只是不轉不彈。
 */
export default function ScrollTop() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const evaluate = () => {
      const y = window.scrollY;
      const threshold = window.innerHeight * 2;

      // 往上捲 = 在找東西；往下捲 = 正在讀，不要打斷
      if (y < threshold) setVisible(false);
      else if (y < lastY - 4) setVisible(true);
      else if (y > lastY + 4) setVisible(false);

      lastY = y;
      ticking = false;
    };

    const onScroll = () => {
      // 捲動事件一秒可以觸發上百次，交給 rAF 節流
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(evaluate);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          aria-label={t("scrollTop")}
          title={t("scrollTop")}
          onClick={() =>
            window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" })
          }
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.8, rotate: -25 }}
          animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8, transition: { duration: 0.15 } }}
          transition={{ type: "spring", stiffness: 340, damping: 24 }}
          whileHover={reduceMotion ? undefined : { y: -3 }}
          whileTap={reduceMotion ? undefined : { scale: 0.92 }}
          /*
           * 位置刻意避開右下角最底部 —— 手機上那裡是瀏覽器工具列，
           * 而且要讓開 Toast（Toast 在 z-60，這顆在 z-40，堆疊時
           * Toast 蓋在上面是對的：訊息比捷徑重要）。
           */
          className="fixed bottom-6 right-4 z-40 flex size-11 items-center justify-center rounded-full nm-lg text-ink-soft transition-colors hover:text-ink sm:bottom-8 sm:right-6"
        >
          <ArrowUp className="size-5" strokeWidth={2.2} />
        </motion.button>
      )}
    </AnimatePresence>
  );
}
